// 스킬 타입
export const SKILL_TARGET_TYPE = {
  SINGLE: 'SINGLE', // 단일 대상
  AOE: 'AOE', // 광역
  IGNORE_DEFENSE: 'IGNORE_DEFENSE', // 방어 무시
} as const;

export type SKILL_TARGET_TYPE_VALUE =
  typeof SKILL_TARGET_TYPE[keyof typeof SKILL_TARGET_TYPE];

// 공격 패턴 아이템
export interface IAttackPatternItem {
  skillId: string; // 'basic' 또는 스킬 ID
  skillName: string; // 표시용 이름
}

// 공격 패턴
export interface IAttackPattern {
  id: string;
  name: string; // 패턴명 (패턴A, 패턴B 등)
  sequence: IAttackPatternItem[]; // 공격 순서
}

// 체력 구간별 패턴
export interface IHpPhasePattern {
  hpRange: string; // 'HP 100-70%'
  hpMin: number; // 최소 HP (%)
  hpMax: number; // 최대 HP (%)
  patternId: string; // 사용할 패턴 ID
}

// 몬스터 스킬
export interface IMonsterSkill {
  id: string;
  name: string; // 스킬명
  /** 스킬 데미지 배율 (1.0 = 기본 공격, 1.5 = 150%). 미설정 시 폴백값 1.2 */
  skillMultiplier?: number;
  targetType: SKILL_TARGET_TYPE_VALUE; // 단일/광역/방어무시
  attackType: string; // 공격/방어
  defenseType: string; // 공격/방어
  attackRange?: number; // 공격범위 (칸 수)
}

// 보상 아이템
export interface IMonsterReward {
  itemId: string; // 아이템 ID
  itemName?: string; // 표시용
  isMainReward: boolean; // 주요 보상 표시
  isGuaranteed: boolean; // 확정 보상
  probability?: number; // 확률 (%)
}

// 몬스터 인터페이스
export interface IMonster {
  id: string;
  title: string; // 몬스터명 (Droppable에서 사용)
  maxHp: number; // 최대 HP
  speed: number; // 속도 (낮을수록 빠름)
  attack?: number; // 공격력 (미설정 시 폴백값 15)
  defense?: number; // 방어력 (미설정 시 폴백값 8)
  critical?: number; // 크리티컬 확률 % (미설정 시 폴백값 10)
  movement?: number; // 이동력 (칸 수, 예: 1칸)
  size?: number; // 크기 (마스레이드에서 차지하는 칸의 크기, 기본값 1)
  imgURL?: string; // 몬스터 이미지
  description?: string; // 설명

  skills: IMonsterSkill[]; // 스킬 목록
  attackPatterns: IAttackPattern[]; // 공격 패턴 목록
  hpPhasePatterns: IHpPhasePattern[]; // 체력 구간별 패턴
  rewards: IMonsterReward[]; // 보상 목록
  dialogues?: string[]; // 턴 소비 시 랜덤 출력 대사 목록

  order: number; // 정렬 순서
  createdAt?: Date;
  updatedAt?: Date;
  authorUid?: string;
  isActive?: boolean;
}

// 스킬 타겟 타입 라벨
export const skillTargetTypeLabels: Record<SKILL_TARGET_TYPE_VALUE, string> = {
  [SKILL_TARGET_TYPE.SINGLE]: '단일스킬',
  [SKILL_TARGET_TYPE.AOE]: '광역스킬',
  [SKILL_TARGET_TYPE.IGNORE_DEFENSE]: '데미지 무시',
};

// ========================================
// 자동 전투 시스템 관련 타입
// ========================================

// 전투 로그 타입
export type BattleLogType =
  | 'encounter'     // 조우
  | 'player_attack' // 플레이어 공격
  | 'monster_attack'// 몬스터 공격
  | 'player_skill'  // 플레이어 스킬 사용
  | 'monster_skill' // 몬스터 스킬 사용
  | 'damage'        // 데미지
  | 'heal'          // 회복
  | 'buff'          // 버프
  | 'debuff'        // 디버프
  | 'victory'       // 승리
  | 'defeat'        // 패배
  | 'draw';         // 무승부

// 전투 로그 아이템
export interface IBattleLog {
  id: string;
  turn: number;
  type: BattleLogType;
  actor: 'player' | 'monster';
  message: string;
  damage?: number;
  heal?: number;
  skillName?: string;
  timestamp: number;
  /** 이 로그 직후 플레이어 HP (HP 바 턴별 애니메이션용) */
  playerHpAfter?: number;
  /** 이 로그 직후 몬스터 HP (HP 바 턴별 애니메이션용) */
  monsterHpAfter?: number;
}

// 전투 참가자 상태
export interface IBattleParticipant {
  id: string;
  name: string;
  imgURL?: string;
  maxHp: number;
  currentHp: number;
  maxMp?: number;
  currentMp?: number;
  attack: number;
  defense: number;
  critical: number;
  speed: number;
  skills?: {
    id: string;
    name: string;
    /** 스킬 데미지 배율 (1.0 = 기본 공격과 동일, 1.5 = 150%) */
    skillMultiplier?: number;
    mpCost?: number;
    order?: number;
  }[];
}

// 전투 상태
export interface IBattleState {
  isActive: boolean;
  turn: number;
  maxTurns: number;
  player: IBattleParticipant;
  monster: IBattleParticipant;
  logs: IBattleLog[];
  result?: 'victory' | 'defeat' | 'draw' | null;
  rewards?: IMonsterReward[];
}

// ========================================
// 스킬 속성 (레이드 전투용 — 캐릭터/몬스터 공용)
// ========================================

export const SKILL_TYPE = {
  SINGLE_ATTACK: 'SINGLE_ATTACK',
  AOE_ATTACK: 'AOE_ATTACK',
  SINGLE_HEAL: 'SINGLE_HEAL',
  AOE_HEAL: 'AOE_HEAL',
  SHIELD: 'SHIELD',
  BUFF_ATTACK: 'BUFF_ATTACK',
  BUFF_DEFENSE: 'BUFF_DEFENSE',
  DEBUFF_ATTACK: 'DEBUFF_ATTACK',
  DEBUFF_DEFENSE: 'DEBUFF_DEFENSE',
  DRAIN: 'DRAIN',
  STUN: 'STUN',
  POISON: 'POISON',
  COUNTER: 'COUNTER',
  REVIVE: 'REVIVE',
} as const;

export type SkillTypeValue = typeof SKILL_TYPE[keyof typeof SKILL_TYPE];

export const skillTypeLabels: Record<SkillTypeValue, string> = {
  [SKILL_TYPE.SINGLE_ATTACK]: '단일 공격',
  [SKILL_TYPE.AOE_ATTACK]: '광역 공격 (다수 적)',
  [SKILL_TYPE.SINGLE_HEAL]: '단일 치료',
  [SKILL_TYPE.AOE_HEAL]: '광역 치료 (다수 아군)',
  [SKILL_TYPE.SHIELD]: '1턴 방어 (피해 무효)',
  [SKILL_TYPE.BUFF_ATTACK]: '공격력 버프',
  [SKILL_TYPE.BUFF_DEFENSE]: '방어력 버프',
  [SKILL_TYPE.DEBUFF_ATTACK]: '공격력 디버프 (적)',
  [SKILL_TYPE.DEBUFF_DEFENSE]: '방어력 디버프 (적)',
  [SKILL_TYPE.DRAIN]: 'HP 흡수 (공격 + 회복)',
  [SKILL_TYPE.STUN]: '기절 (적 1턴 행동불능)',
  [SKILL_TYPE.POISON]: '독 (턴마다 지속 피해)',
  [SKILL_TYPE.COUNTER]: '반격 (피해 반사)',
  [SKILL_TYPE.REVIVE]: '부활 (쓰러진 아군 부활)',
};

export const skillTypeDescriptions: Record<SkillTypeValue, string> = {
  [SKILL_TYPE.SINGLE_ATTACK]: '지정한 적 1명에게 공격',
  [SKILL_TYPE.AOE_ATTACK]: '여러 적에게 동시 공격 (대상 수 설정 가능)',
  [SKILL_TYPE.SINGLE_HEAL]: '아군 1명의 HP 회복',
  [SKILL_TYPE.AOE_HEAL]: '여러 아군의 HP 동시 회복 (대상 수 설정 가능)',
  [SKILL_TYPE.SHIELD]: '사용 턴 동안 받는 모든 피해를 0으로 상쇄',
  [SKILL_TYPE.BUFF_ATTACK]: '아군의 공격력을 일정 턴 동안 증가',
  [SKILL_TYPE.BUFF_DEFENSE]: '아군의 방어력을 일정 턴 동안 증가',
  [SKILL_TYPE.DEBUFF_ATTACK]: '적의 공격력을 일정 턴 동안 감소',
  [SKILL_TYPE.DEBUFF_DEFENSE]: '적의 방어력을 일정 턴 동안 감소',
  [SKILL_TYPE.DRAIN]: '적에게 피해를 주고, 준 피해의 일부만큼 HP 회복',
  [SKILL_TYPE.STUN]: '적 1명을 1턴 행동불능 상태로 만듦',
  [SKILL_TYPE.POISON]: '적에게 일정 턴 동안 매 턴 피해를 줌',
  [SKILL_TYPE.COUNTER]: '피해를 받으면 일정 비율로 반사',
  [SKILL_TYPE.REVIVE]: '쓰러진 아군 1명을 일정 HP로 부활',
};

// 전투 설정 (관리자가 설정)
export interface IBattleConfig {
  maxTurns: number;                // 최대 턴 수
  criticalMultiplier: number;      // 크리티컬 데미지 배율 (기본 1.5)
  criticalStatCap?: number;        // 최대 치명타 확률 도달 기준 스탯 (기본 300)
  maxCriticalChance?: number;      // 최대 치명타 확률 % (기본 99)
  defenseReduction: number;        // 방어력 데미지 감소율 % (기본 50)
  baseHitChance: number;           // 기본 명중률 % (기본 90)
  // ── 전투 확장 상수 ────────────
  aoeDamageRatio: number;          // 광역 공격 데미지 비율 (기본 0.4)
  aoeHealRatio: number;            // 광역 치료 회복 비율 (기본 0.3)
  healCriticalMultiplier: number;  // 치료 크리티컬 배율 (기본 1.3)
  damageVariance: number;          // 데미지 변동폭 (기본 0.1 = ±10%)
  healVariance: number;            // 치료 변동폭 (기본 0.05 = ±5%)
  healAttackRatio: number;         // 치료 시 ATK → 회복량 변환율 (기본 0.5)
  minDamage: number;               // 최소 데미지 (기본 1)
  minHeal: number;                 // 최소 회복량 (기본 1)
}

export const defaultBattleConfig: IBattleConfig = {
  maxTurns: 10,
  criticalMultiplier: 1.5,
  criticalStatCap: 300,
  maxCriticalChance: 99,
  defenseReduction: 50,
  baseHitChance: 90,
  aoeDamageRatio: 0.4,
  aoeHealRatio: 0.3,
  healCriticalMultiplier: 1.3,
  damageVariance: 0.1,
  healVariance: 0.05,
  healAttackRatio: 0.5,
  minDamage: 1,
  minHeal: 1,
};
